/**
 * The platform panel's rules, as pure functions the screens and the tests share.
 *
 * The lifecycle moves mirror `platform_admin/operations.py`. The server is the authority:
 * these only decide which buttons are offered, so a stale client cannot make a move the
 * server would refuse, and the refusal text still comes from the server.
 */
import { t } from "@/i18n";
import { ApiError } from "@/lib/api";

import type { DemoStatus, TenantStatus } from "@/api/platform";

export const TENANT_STATUS_LABEL: Readonly<Record<TenantStatus, string>> = {
  get active() {
    return t("b12plat.faol");
  },
  get suspended_billing() {
    return t("b12plat.tolov-boyicha-toxtatilgan");
  },
  get suspended_admin() {
    return t("b12plat.administrator-toxtatgan");
  },
  get closed() {
    return t("b12plat.yopilgan");
  },
};

/** Which status a platform admin may move an agency to from each state. */
const MANUAL_MOVES: Readonly<Record<TenantStatus, readonly TenantStatus[]>> = {
  active: ["suspended_admin", "closed"],
  suspended_billing: ["active", "suspended_admin", "closed"],
  suspended_admin: ["active", "closed"],
  closed: [],
};

export function allowedTenantMoves(from: TenantStatus): readonly TenantStatus[] {
  return MANUAL_MOVES[from];
}

export function canMoveTenant(from: TenantStatus, to: TenantStatus): boolean {
  return MANUAL_MOVES[from].includes(to);
}

/** A reason is mandatory for every lifecycle change; whitespace alone does not count. */
export function reasonIsPresent(reason: string): boolean {
  return reason.trim().length > 0;
}

export const DEMO_STATUS_LABEL: Readonly<Record<DemoStatus, string>> = {
  get new() {
    return t("b12plat.yangi");
  },
  get contacted() {
    return t("b12plat.boglanildi");
  },
  get demo_scheduled() {
    return t("b12plat.demo-belgilandi");
  },
  get converted() {
    return t("b12plat.agentlikka-aylantirildi");
  },
  get rejected() {
    return t("b12plat.rad-etildi");
  },
};

/** Targets a sales person may choose by hand. `converted` is reached only by converting. */
export const DEMO_MANUAL_TARGETS: readonly DemoStatus[] = [
  "new",
  "contacted",
  "demo_scheduled",
  "rejected",
];

export function canChangeDemo(current: DemoStatus, to: DemoStatus): boolean {
  if (current === "converted" || to === "converted" || to === current) {
    return false;
  }
  return DEMO_MANUAL_TARGETS.includes(to);
}

export function canConvertDemo(current: DemoStatus, hasTenant: boolean): boolean {
  return current !== "converted" && current !== "rejected" && !hasTenant;
}

export { formatUzs } from "@/lib/format";

/** Splits the branch field on new lines and commas, trims, and drops blanks and repeats. */
export function parseBranches(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\n,]/)) {
    const name = part.trim();
    if (name !== "" && !seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      out.push(name);
    }
  }
  return out;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** The server's own sentence when it has one; a fixed fallback otherwise. */
export function refusalText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && typeof error.payload === "object" && error.payload !== null) {
    const detail = (error.payload as Record<string, unknown>).detail;
    if (typeof detail === "string" && detail !== "") {
      return detail;
    }
  }
  return fallback;
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** A supplier account's last check, in Uzbek. The codes are the backend's `ProviderHealthStatus`. */
export const PROVIDER_STATUS_LABEL: Readonly<Record<string, string>> = {
  get ok() {
    return t("b12plat.ishlayapti");
  },
  get auth_failed() {
    return t("b12plat.kirish-rad-etildi");
  },
  get unavailable() {
    return t("b12plat.javob-bermadi");
  },
  get protocol_error() {
    return t("b12plat.javob-tushunarsiz");
  },
};

/** The label for a check, or t("b12plat.tekshirilmagan") when no check has run yet. */
export function providerStatusLabel(code: string): string {
  return code === "" ? t("b12plat.tekshirilmagan") : (PROVIDER_STATUS_LABEL[code] ?? code);
}

/**
 * How long a supplier has been failing, as a badge tone. The canary alerts the owner at the
 * third failure in a row (D169), so that is where the badge turns red.
 */
export const STREAK_ALERT_AT = 3;

export function streakTone(failures: number): "danger" | "warning" | "neutral" {
  if (failures >= STREAK_ALERT_AT) return "danger";
  if (failures > 0) return "warning";
  return "neutral";
}

/** The journal's severity, as the badge tone it is shown with. */
export function severityTone(severity: string): "danger" | "warning" | "neutral" {
  if (severity === "critical") return "danger";
  if (severity === "warning") return "warning";
  return "neutral";
}

/** Languages the create form offers. Only one is shipped (D272); the server refuses the rest. */
export const CREATE_LANGUAGES: readonly string[] = ["uz-latn"];

/** The two currencies an agency can report in (the closed list the server enforces). */
export const CREATE_CURRENCIES: readonly ("UZS" | "USD")[] = ["UZS", "USD"];

/**
 * Whether the panel shows the password link to copy rather than saying the owner has it by
 * e-mail (D580). The link is returned in both cases, so the panel never has to ask again.
 */
export function showsLinkToCopy(created: { invitation_sent: boolean }): boolean {
  return !created.invitation_sent;
}

/** The list's last-activity cell: the date of the newest sign-in, or "Kirmagan" for none. */
export function lastActivityText(lastActivityAt: string | null): string {
  return lastActivityAt === null ? t("b12plat.kirmagan") : lastActivityAt.slice(0, 10);
}

/** The list's package cell: the package's name, or "Paketsiz" when the agency has none. */
export function tariffText(tariff: string | null): string {
  return tariff ?? t("b12plat.paketsiz");
}

/** A count of channels that cannot carry messages reads as danger only when it is not zero. */
export function channelAttentionTone(count: number): "danger" | "neutral" {
  return count > 0 ? "danger" : "neutral";
}

/** The calendar month so far, as the API's inclusive dates. */
export function monthWindow(now: Date): { from: string; to: string } {
  const pad = (value: number): string => String(value).padStart(2, "0");
  const prefix = `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}`;
  return { from: `${prefix}-01`, to: `${prefix}-${pad(now.getDate())}` };
}

export interface AiSpendTenant {
  tenant: string;
  name: string;
  today: number;
  month: number;
  model: string;
  modelShare: number;
}

/** One line per agency: today's tokens, the month's, and the model that did most of them. */
export function aiSpendByTenant(
  rows: readonly {
    tenant: string;
    tenant_name: string;
    day: string;
    model: string;
    prompt_tokens: number;
    completion_tokens: number;
  }[],
  today: string,
): AiSpendTenant[] {
  const byTenant = new Map<string, { name: string; today: number; month: number; models: Map<string, number> }>();
  for (const row of rows) {
    const tokens = row.prompt_tokens + row.completion_tokens;
    const entry = byTenant.get(row.tenant) ?? { name: row.tenant_name, today: 0, month: 0, models: new Map<string, number>() };
    entry.month += tokens;
    if (row.day === today) entry.today += tokens;
    entry.models.set(row.model, (entry.models.get(row.model) ?? 0) + tokens);
    byTenant.set(row.tenant, entry);
  }
  return [...byTenant.entries()]
    .map(([tenant, entry]) => {
      const [model, used] = [...entry.models.entries()].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
      return {
        tenant,
        name: entry.name,
        today: entry.today,
        month: entry.month,
        model,
        modelShare: entry.month === 0 ? 0 : Math.round((used / entry.month) * 100),
      };
    })
    .sort((a, b) => b.month - a.month);
}
